import { Fragment, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Check, Pencil, Plus, Trash2, TriangleAlert, X } from 'lucide-react'

import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { useSegmentNames } from '@/hooks/useSegmentNames'
import {
  hasIgnoredSegmentRows,
  readIncludedSegments,
  writeIncludedSegments,
  type ObjectRef,
  type Targeting,
  type UserSegmentTarget,
} from '@/services/targeting/targeting'
import type { SegmentRef } from '@/services/targeting/targetingLookupApi'
import { enumLabel } from '@/utils/enumLabel'
import { SegmentSearch } from './SegmentSearch'

export interface SegmentTargetEditorProps {
  /** The line item's `userSegmentTargets`, row for row. */
  value: UserSegmentTarget[]
  onChange: (value: UserSegmentTarget[]) => void
  /** The whole targeting being edited, whose inventory narrows the segments offered. */
  targeting?: Targeting
}

type Mode = 'include' | 'groups'

/** A row's two lists: the segments a user must be in, and its NOT block. */
type Field = 'userSegments' | 'notUserSegments'

/** What a picked segment is saved as. The API reads only the id; the rest is for display. */
const toRef = (segment: SegmentRef): SegmentRef => ({
  id: segment.id,
  name: segment.name,
  description: segment.description,
  dataSource: segment.dataSource,
})

const without = (refs: ObjectRef[], id: string) => refs.filter((ref) => ref.id !== id)
const uniqueById = (refs: ObjectRef[]) => [...new Map(refs.map((ref) => [ref.id, ref])).values()]
const holds = (refs: ObjectRef[] | undefined, id: string) => !!refs?.some((ref) => ref.id === id)

/**
 * Targets the audience segments a user belongs to, in one of two views over
 * the same rows, as the old UI has them. Include is one list of alternatives,
 * which covers most line items. Groups shows the rows themselves, for what a
 * list cannot hold: segments a user must all be in, segments ruled out, and
 * alternatives between such groups. Segments are picked from the table, and
 * what is picked is listed under it.
 */
export function SegmentTargetEditor({ value, onChange, targeting }: SegmentTargetEditorProps) {
  const { t } = useTranslation()
  const what = t('targeting.types.userSegment.plural').toLowerCase()
  const included = readIncludedSegments(value)
  const [mode, setMode] = useState<Mode>(() => (included ? 'include' : 'groups'))
  // Rows the list cannot show are only ever shown as groups.
  const shown: Mode = included ? mode : 'groups'
  /** The group being edited: the one AND and AND NOT add to. */
  const [activeGroup, setActiveGroup] = useState(() => Math.max(0, value.length - 1))
  const active = Math.min(activeGroup, value.length - 1)

  /** Every segment the rows name, included and NOT, each once. */
  const picked = useMemo(
    () =>
      uniqueById(
        value.flatMap((row) => [...(row.userSegments ?? []), ...(row.notUserSegments ?? [])])
      ),
    [value]
  )
  const pickedIds = useMemo(() => picked.map((ref) => ref.id), [picked])
  // A segment the API sent as a bare id still reads as its name here, and
  // carries its type and publishers for the table's "Show selected".
  const { fill } = useSegmentNames(pickedIds)

  // ─── Include ──────────────────────────────────────────────────────────

  const setIncluded = (segments: ObjectRef[]) => onChange(writeIncludedSegments(segments))

  // Picking an included segment again takes it out.
  const includeActions = (segment: SegmentRef) => {
    const list = included ?? []
    const isIncluded = holds(list, segment.id)
    return (
      <PickButton
        label={t('targeting.includeShort')}
        pressed={isIncluded}
        onClick={() =>
          setIncluded(isIncluded ? without(list, segment.id) : [...list, toRef(segment)])
        }
      />
    )
  }

  // ─── Groups ───────────────────────────────────────────────────────────

  /** A group emptied of segments goes with them. */
  const setRows = (rows: UserSegmentTarget[]) =>
    onChange(rows.filter((row) => row.userSegments?.length || row.notUserSegments?.length))

  const addToGroup = (index: number, field: Field, segment: SegmentRef) =>
    setRows(
      value.map((row, i) =>
        i === index ? { ...row, [field]: [...(row[field] ?? []), toRef(segment)] } : row
      )
    )

  const removeFromGroup = (index: number, field: Field, id: string) => {
    const row = value[index]
    const left = without(row[field] ?? [], id)
    const other = field === 'userSegments' ? row.notUserSegments : row.userSegments
    if (!left.length && !other?.length) {
      removeGroup(index)
      return
    }
    setRows(value.map((r, i) => (i === index ? { ...r, [field]: left } : r)))
  }

  /** A new group holding one segment — or ruling it out, with no segments of its own. */
  const addGroup = (field: Field, segment: SegmentRef) => {
    onChange([...value, { [field]: [toRef(segment)] }])
    setActiveGroup(value.length)
  }

  const removeGroup = (index: number) => {
    setRows(value.filter((_, i) => i !== index))
    // The group being edited keeps its place when one above it goes.
    if (index < active) setActiveGroup(active - 1)
  }

  const groupActions = (segment: SegmentRef) => {
    const group = value[active] as UserSegmentTarget | undefined
    const number = active + 1
    const inGroup =
      !!group && (holds(group.userSegments, segment.id) || holds(group.notUserSegments, segment.id))
    const blocked = !group || inGroup
    const blockedHelp = group
      ? t('targeting.segment.inGroup', { number })
      : t('targeting.segment.startWithOr')
    return (
      <>
        <PickButton
          label={t('targeting.segment.and')}
          blocked={blocked}
          help={blocked ? blockedHelp : t('targeting.segment.andHelp', { number })}
          onClick={() => addToGroup(active, 'userSegments', segment)}
        />
        <PickButton
          label={`${t('targeting.segment.and')} ${t('targeting.segment.not')}`}
          negative
          blocked={blocked}
          help={blocked ? blockedHelp : t('targeting.segment.andNotHelp', { number })}
          onClick={() => addToGroup(active, 'notUserSegments', segment)}
        />
        <PickButton
          label={t('targeting.segment.or')}
          help={t('targeting.segment.orHelp')}
          onClick={() => addGroup('userSegments', segment)}
        />
        <PickButton
          label={`${t('targeting.segment.or')} ${t('targeting.segment.not')}`}
          negative
          help={t('targeting.segment.orNotHelp')}
          onClick={() => addGroup('notUserSegments', segment)}
        />
      </>
    )
  }

  /** One group's segments, read left to right with the logic between them. */
  const groupChips = (index: number, row: UserSegmentTarget) => {
    const includes = fill(row.userSegments)
    const nots = fill(row.notUserSegments)
    const and = t('targeting.segment.and')
    return (
      <div className="flex flex-wrap items-center gap-1.5">
        {includes.map((segment, i) => (
          <Fragment key={`in-${segment.id}`}>
            {i > 0 && <Connective>{and}</Connective>}
            <SegmentChip
              segment={segment}
              onRemove={() => removeFromGroup(index, 'userSegments', segment.id)}
            />
          </Fragment>
        ))}
        {/* The NOT block is one condition — it turns away users who are in all
            of its segments together — so more than one is bracketed. */}
        {nots.length > 0 && (
          <Connective>
            {includes.length > 0 && `${and} `}
            {t('targeting.segment.not')}
            {nots.length > 1 && ' ('}
          </Connective>
        )}
        {nots.map((segment, i) => (
          <Fragment key={`not-${segment.id}`}>
            {i > 0 && <Connective>{and}</Connective>}
            <SegmentChip
              segment={segment}
              excluded
              onRemove={() => removeFromGroup(index, 'notUserSegments', segment.id)}
            />
          </Fragment>
        ))}
        {nots.length > 1 && <Connective>)</Connective>}
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <Tabs
          value={shown}
          onValueChange={(next) => {
            setMode(next as Mode)
            setActiveGroup(value.length - 1)
          }}
        >
          <TabsList>
            <TabsTrigger value="include" disabled={!included}>
              {t('targeting.segment.include')}
            </TabsTrigger>
            <TabsTrigger value="groups">{t('targeting.segment.groups')}</TabsTrigger>
          </TabsList>
        </Tabs>
        {!included && (
          <span className="text-xs text-muted-foreground">
            {t('targeting.segment.includeUnavailable')}
          </span>
        )}
      </div>

      <SegmentSearch
        targeting={targeting}
        selected={fill(picked)}
        actions={shown === 'include' ? includeActions : groupActions}
        hint={
          shown === 'groups' && (
            <p className="text-xs text-muted-foreground">
              {value.length
                ? t('targeting.segment.addingTo', { number: active + 1 })
                : t('targeting.segment.startWithOr')}
            </p>
          )
        }
      />

      {shown === 'include' && included ? (
        <SelectionBox label={t('targeting.selected')}>
          {included.length === 0 ? (
            <p className="text-xs text-muted-foreground">{t('targeting.noneSelected', { what })}</p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {fill(included).map((segment) => (
                <SegmentChip
                  key={segment.id}
                  segment={segment}
                  onRemove={() => setIncluded(without(included, segment.id))}
                />
              ))}
            </div>
          )}
        </SelectionBox>
      ) : (
        <div className="flex flex-col gap-3">
          <p className="text-xs text-muted-foreground">{t('targeting.segment.groupsHelp')}</p>

          {hasIgnoredSegmentRows(value) && (
            <Alert variant="warning">
              <TriangleAlert />
              <AlertDescription>{t('targeting.segment.ignoredRows')}</AlertDescription>
            </Alert>
          )}

          {value.length === 0 && (
            <SelectionBox label={t('targeting.selected')}>
              <p className="text-xs text-muted-foreground">
                {t('targeting.segment.noGroups')} {t('targeting.segment.startWithOr')}
              </p>
            </SelectionBox>
          )}

          {value.map((row, index) => {
            const number = index + 1
            const editing = index === active
            return (
              <Fragment key={index}>
                {index > 0 && (
                  <div className="flex items-center gap-2 text-xs font-semibold text-muted-foreground">
                    <span className="h-px flex-1 bg-border" />
                    {t('targeting.segment.or')}
                    <span className="h-px flex-1 bg-border" />
                  </div>
                )}
                <SelectionBox
                  label={
                    editing
                      ? `${t('targeting.segment.group', { number })} · ${t('targeting.segment.editing')}`
                      : t('targeting.segment.group', { number })
                  }
                  active={editing}
                  actions={
                    <>
                      <Button
                        type="button"
                        variant={editing ? 'secondary' : 'ghost'}
                        size="icon-xs"
                        aria-label={t('targeting.segment.editGroup', { number })}
                        aria-pressed={editing}
                        className={cn(editing && 'text-(--ad-primary)')}
                        onClick={() => setActiveGroup(index)}
                      >
                        <Pencil />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-xs"
                        aria-label={t('targeting.segment.removeGroup', { number })}
                        onClick={() => removeGroup(index)}
                      >
                        <Trash2 />
                      </Button>
                    </>
                  }
                >
                  {groupChips(index, row)}
                </SelectionBox>
              </Fragment>
            )
          })}
        </div>
      )}
    </div>
  )
}

/** The id when a name hides it, and where the segment came from. */
const segmentMeta = (segment: SegmentRef) =>
  [segment.name && segment.id, segment.dataSource && enumLabel(segment.dataSource)]
    .filter(Boolean)
    .join(' · ')

/**
 * A box of picked segments under a small heading — drawn like the inventory
 * editor's "Selected" box. The group being edited is outlined.
 */
function SelectionBox({
  label,
  active,
  actions,
  children,
}: {
  label: string
  active?: boolean
  /** Buttons at the right of the heading. */
  actions?: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <div
      className={cn(
        'flex flex-col gap-2 rounded-md border p-3',
        active ? 'border-(--ad-primary) ring-1 ring-(--ad-primary)' : 'border-(--ad-primary-subtle)'
      )}
    >
      <div className="flex min-h-6 items-center gap-2">
        <span
          className={cn(
            'text-[11px] font-semibold tracking-wider uppercase',
            active ? 'text-(--ad-primary)' : 'text-muted-foreground'
          )}
        >
          {label}
        </span>
        {actions && <div className="ml-auto flex items-center gap-0.5">{actions}</div>}
      </div>
      {children}
    </div>
  )
}

/** The AND, NOT and brackets between a group's chips. */
function Connective({ children }: { children: React.ReactNode }) {
  return (
    <span className="text-[11px] font-semibold tracking-wider text-muted-foreground uppercase">
      {children}
    </span>
  )
}

/**
 * One picked segment as a chip — green to include, red and struck through to
 * exclude, the colours of the table's buttons and the targeting summary.
 */
function SegmentChip({
  segment,
  excluded,
  onRemove,
}: {
  segment: SegmentRef
  excluded?: boolean
  onRemove: () => void
}) {
  const { t } = useTranslation()
  return (
    <Badge
      variant={excluded ? 'red' : 'success'}
      className="gap-1.5"
      title={segmentMeta(segment) || undefined}
    >
      <span className={cn(excluded && 'line-through')}>{segment.name || segment.id}</span>
      <button
        type="button"
        onClick={onRemove}
        aria-label={t('common.remove')}
        className="opacity-60 hover:opacity-100"
      >
        <X className="size-3" />
      </button>
    </Badge>
  )
}

/**
 * A button in a segment's table row. Pressed, it shows the segment is already
 * picked that way — green to include, red to exclude, the colours the targeting
 * summary uses. `help` explains it on hover, and why when it is blocked.
 */
function PickButton({
  label,
  pressed = false,
  negative,
  blocked,
  help,
  onClick,
}: {
  label: string
  pressed?: boolean
  negative?: boolean
  blocked?: boolean
  help?: string
  onClick: () => void
}) {
  const button = (
    <Button
      type="button"
      variant="outline"
      size="xs"
      aria-pressed={pressed}
      disabled={blocked}
      onClick={onClick}
      className={cn(
        pressed &&
          (negative
            ? 'border-(--ad-badge-red-border) bg-(--ad-badge-red-bg) text-(--ad-badge-red)'
            : 'border-(--ad-badge-green-border) bg-(--ad-badge-green-bg) text-(--ad-badge-green)')
      )}
    >
      {pressed ? <Check /> : <Plus />}
      {label}
    </Button>
  )
  if (!help) return button
  return (
    <Tooltip>
      {/* A disabled button takes no pointer events, so the wrapper carries the hover. */}
      <TooltipTrigger asChild>
        <span className="inline-flex" tabIndex={blocked ? 0 : undefined}>
          {button}
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">{help}</TooltipContent>
    </Tooltip>
  )
}
